import {
	MessageType,
	SharedFileMessage,
	SharedFileMessageV2,
	JobRequestCustomV1,
} from "../../../../lib/store/Realtime/message.types";
import {
	JobRequestMessageV1,
	TextMessage,
} from "../../../../lib/store/Realtime/message.types";
import { Tables } from "@/lib/supabase/supabaseTypesImport";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import { getMessageContent } from "./message-preview";
import { getDayAndTime } from "../../lib/utils/utils-functions";
import { ProfilesForChat } from "@/lib/types/profile.types";
import { stringAvatar } from "../../lib/utils/avatar-functions";
import React from "react";
import { JobRequestCustomV1Block } from "./messages/job-request-custom/job-request-custom";
import { useFileHandler } from "../../../../lib/hooks/FileHandler/useFileHandler";
import { FileGalleryInMessage } from "./messages/job-request-custom/components/file-in-message/file-gallery-in-message";
import { ConnectionRequestBlock } from "./messages/ConnectionRequestBlock";
import {
	DeliveryCreatedBlock,
	DeliveryStatusUpdatedBlock,
} from "./messages/delivery-message-block.component";
import MessageActions from "./message-actions";
import { OdontogramInMessage } from "./messages/job-request-custom/components/odontogram-in-message";
import { useConnectStore } from "../../store/Connect";
import { Avatar } from "@mui/material";
import { Loading } from "@/components/src/animations/loading";
import { useExportPdf } from "../../hooks/useExportPdf";
import { createSecurePatientName } from "../../lib/utils/utils-functions";
import { useFormsStore } from "../../store/Forms";
import { CustomFormSubmissionTemplate } from "../../pdf-templates/pdf-templates/custom-form-submission.template";
import { JobRequestV1SubmissionTemplate } from "../../pdf-templates/pdf-templates/job-request-v1-submission.template";
import { showNotification } from "../../store/Central/selectors";
import { useCentralStore } from "../../store/Central";
import {
	getClientDisplayName,
	getProfileDisplayName,
} from "@/lib/utils/client-utils";
import { supabase } from "../../../../lib/supabase";
/**
 * @param messageAt: string in the format "2024-01-01T00:00:00Z" as in the database!
 */
export const Message: React.FC<{
	message: Tables<"messages_with_actions">;
	messageAt: string;
	user: ProfilesForChat;
	onShareFile: (fileId: string) => void;
	onUnshareFile: (fileId: string) => void;
	onAcceptJobRequest?: (
		messageContent?: JobRequestCustomV1 | null
	) => Promise<boolean>;
	onDenyJobRequest?: () => Promise<boolean>;
	currentOrganizationId: string;
	otherOrganizationId: string;
	isAdmin: boolean;
}> = ({
	message,
	currentOrganizationId,
	otherOrganizationId,
	messageAt,
	user,
	onShareFile,
	onUnshareFile,
	onAcceptJobRequest,
	onDenyJobRequest,
	isAdmin,
}) => {
	const messageContent = message?.content as string;
	if (!messageContent) return null;
	const content = getMessageContent(messageContent);
	const { sx, children } = stringAvatar(
		user ? getProfileDisplayName(user) || "UN" : "UN"
	);

	const { brandsByOrgId } = useConnectStore.getState();
	const brandName = brandsByOrgId[user.organization_id ?? ""]?.name;

	// Get client name from clientsLookup
	const { clientsLookup } = useCentralStore((state) => ({
		clientsLookup: state.clientsLookup,
	}));

	// Find client by organization_id (use user.organization_id since user is the sender)
	const senderClient = Object.values(clientsLookup).find(
		(client) => client?.connected_organization_id === otherOrganizationId
	);
	const senderClientName = senderClient
		? getClientDisplayName(senderClient)
		: brandName;

	const messageOfCurrentOrg = currentOrganizationId === otherOrganizationId;

	const { exportPdf } = useExportPdf();
	const formsByOrgId = useFormsStore((state) => state.formsByOrgId);

	// PDF export handler for both custom and default form messages
	const handleExportFormPdf = async () => {
		if (
			content.type !== MessageType.JOB_REQUEST_CUSTOM_V1 &&
			content.type !== MessageType.JOB_REQUEST_V1
		)
			return;

		try {
			let documentComponent: React.ReactNode;

			if (content.type === MessageType.JOB_REQUEST_CUSTOM_V1) {
				// Handle custom form
				const customFormContent = content as JobRequestCustomV1;

				// Find form template
				const formTemplate = Object.values(formsByOrgId)
					.flatMap((orgForms) => Object.values(orgForms))
					.find(
						(form: any) =>
							form.id === customFormContent.customFormId
					);

				documentComponent = (
					<CustomFormSubmissionTemplate
						content={customFormContent}
						formTemplate={formTemplate as any}
						senderOrgName={senderClientName}
					/>
				);
			} else {
				// Handle default form (JOB_REQUEST_V1)
				const defaultFormContent = content as JobRequestMessageV1;

				// Fetch file names if there are file_ids
				let fileNames: string[] = [];
				if (
					defaultFormContent.file_ids &&
					defaultFormContent.file_ids.length > 0
				) {
					const { data: fileData } = await supabase
						.from("files")
						.select("file_name")
						.in("id", defaultFormContent.file_ids);

					if (fileData) {
						fileNames = fileData.map(
							(file) => file.file_name ?? ""
						);
					}
				}

				documentComponent = (
					<JobRequestV1SubmissionTemplate
						content={defaultFormContent}
						senderOrgName={senderClientName}
						files={fileNames}
					/>
				);
			}

			const result = await exportPdf({
				documentComponent,
			});

			if (result.success && result.blob) {
				// Open PDF in new window
				const url = URL.createObjectURL(result.blob);
				window.open(url, "_blank");

				// Clean up the URL after a delay to ensure it's loaded
				setTimeout(() => URL.revokeObjectURL(url), 100);

				showNotification({
					message: "PDF in neuem Fenster geöffnet",
					type: "success",
				});
			} else {
				showNotification({
					message: result.error || "Fehler beim Erstellen des PDFs",
					type: "error",
				});
			}
		} catch (error) {
			showNotification({
				message: "Fehler beim Erstellen des PDFs",
				type: "error",
			});
		}
	};

	return (
		<div className="flex gap-3 px-5 py-1 hover:bg-gray-50 group">
			<div className="flex-shrink-0 pt-0.5">
				<Avatar
					style={{
						borderRadius: "0.375rem",
						outlineOffset: 3,
					}}
					sx={{
						width: `36px`,
						height: `36px`,
					}}
					id={user?.profile_id}
				>
					{user?.avatar_url &&
					user?.avatar_url?.split("/")[0] != "public" ? (
						<img
							src={user?.avatar_url}
							alt="Client Avatar"
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
							}}
						/>
					) : (
						<span
							className="flex items-center justify-center w-full h-full font-semibold text-white text-sm"
							style={{ backgroundColor: sx.bgcolor }}
						>
							{children}
						</span>
					)}
				</Avatar>
			</div>

			{/* Message content */}
			<div className="flex-1 min-w-0">
				{/* Username and timestamp */}
				<div className="flex items-baseline gap-2 mb-0.5">
					<span className="font-semibold text-gray-900 text-sm">
						{getProfileDisplayName(user) || "Unbekannter Nutzer"}
					</span>
					<span className="text-xs text-gray-500">
						{getDayAndTime(messageAt)}
					</span>
				</div>

				{content.type === MessageType.JOB_REQUEST_V1 ? (
					<JobRequestMessageV1Block
						content={content}
						messageOfCurrentOrg={messageOfCurrentOrg}
						onShareFile={onShareFile}
						onUnshareFile={onUnshareFile}
						isAdmin={isAdmin}
					/>
				) : content.type === MessageType.JOB_REQUEST_CUSTOM_V1 ? (
					<JobRequestCustomV1Block
						content={content}
						messageOfCurrentOrg={messageOfCurrentOrg}
						onShareFile={onShareFile}
						onUnshareFile={onUnshareFile}
						senderOrgName={senderClientName}
						isAdmin={isAdmin}
					/>
				) : content.type === MessageType.TEXT ? (
					<TextMessageBlock content={content} />
				) : content.type === MessageType.SHARED_FILE ? (
					<SharedFileMessageBlock
						content={content}
						messageOfCurrentOrg={messageOfCurrentOrg}
						onShareFile={onShareFile}
						onUnshareFile={onUnshareFile}
						isAdmin={isAdmin}
					/>
				) : content.type === MessageType.SHARED_FILE_V2 ? (
					<SharedFileMessageV2Block
						content={content}
						messageOfCurrentOrg={messageOfCurrentOrg}
						onShareFile={onShareFile}
						onUnshareFile={onUnshareFile}
						isAdmin={isAdmin}
					/>
				) : content.type === MessageType.JOB_REQUEST_DENIED ? (
					<JobRequestDeniedBlock />
				) : content.type === MessageType.JOB_REQUEST_ACCEPTED ? (
					<JobRequestAcceptedBlock />
				) : content.type === MessageType.DELIVERY_CREATED ? (
					<DeliveryCreatedBlock content={content} />
				) : content.type === MessageType.DELIVERY_STATUS_UPDATED ? (
					<DeliveryStatusUpdatedBlock content={content} />
				) : content.type === MessageType.CONNECTION_REQUEST ? (
					<ConnectionRequestBlock
						content={content}
						brandName={brandName}
					/>
				) : content.type === MessageType.CONNECTION_REQUEST_ACCEPTED ? (
					<ConnectionRequestAcceptedBlock />
				) : content.type === MessageType.CONNECTION_REQUEST_DENIED ? (
					<ConnectionRequestDeniedBlock />
				) : (
					<div>Nachricht konnte nicht formatiert werden.</div>
				)}

				<div className="w-full mt-1">
					<MessageActions
						message={message}
						onAcceptJobRequest={onAcceptJobRequest}
						onDenyJobRequest={onDenyJobRequest}
						onExportFormPdf={handleExportFormPdf}
						currentOrganizationId={currentOrganizationId}
						otherOrganizationId={otherOrganizationId}
					/>
				</div>
			</div>
		</div>
	);
};

const SharedFileMessageBlock: React.FC<{
	content: SharedFileMessage;
	messageOfCurrentOrg: boolean;
	onShareFile: (fileId: string) => void;
	onUnshareFile: (fileId: string) => void;
	isAdmin: boolean;
}> = ({
	content,
	messageOfCurrentOrg,
	onShareFile,
	onUnshareFile,
	isAdmin,
}) => {
	const { files, loading, sharedUntil } = useFileHandler([content.file_id]);

	return (
		<div className="flex flex-col">
			{loading ? (
				<div className="bg-gray-50 h-36 w-36 flex items-center justify-center border border-gray-200 rounded-md animate-pulse">
					<Loading type="skeleton" />
				</div>
			) : (
				<div className="flex flex-col w-full justify-center">
					<FileGalleryInMessage
						files={files}
						sharedUntil={sharedUntil}
						messageOfCurrentOrg={messageOfCurrentOrg}
						onShareFile={onShareFile}
						onUnshareFile={onUnshareFile}
						isAdmin={isAdmin}
					/>
				</div>
			)}
		</div>
	);
};

const SharedFileMessageV2Block: React.FC<{
	content: SharedFileMessageV2;
	messageOfCurrentOrg: boolean;
	onShareFile: (fileId: string) => void;
	onUnshareFile: (fileId: string) => void;
	isAdmin: boolean;
}> = ({
	content,
	messageOfCurrentOrg,
	onShareFile,
	onUnshareFile,
	isAdmin,
}) => {
	const { files, loading, sharedUntil } = useFileHandler(content.file_ids);

	return (
		<div className="flex flex-col">
			{/* File message text */}
			{content.text && (
				<div className="text-sm text-gray-900">
					{parse(DOMPurify.sanitize(content.text ?? ""))}
				</div>
			)}

			<div className="flex flex-col gap-2">
				<div className="text-sm font-semibold text-gray-700 mt-2">
					Angehängte Dateien:
				</div>

				{/* Mehrere Dateien anzeigen */}
				<div className="flex flex-col">
					{loading ? (
						<div className="flex flex-wrap gap-2">
							{files.map((file) => (
								<div
									key={file.id}
									className="bg-gray-50 h-36 w-36 flex items-center justify-center border border-gray-200 rounded-md animate-pulse"
								>
									<Loading type="skeleton" />
								</div>
							))}
						</div>
					) : (
						<div className="flex flex-col w-full justify-center">
							<FileGalleryInMessage
								files={files}
								sharedUntil={sharedUntil}
								messageOfCurrentOrg={messageOfCurrentOrg}
								onShareFile={onShareFile}
								onUnshareFile={onUnshareFile}
								isAdmin={isAdmin}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export const JobRequestMessageV1Block: React.FC<{
	content: JobRequestMessageV1;
	messageOfCurrentOrg: boolean;
	onShareFile: (fileId: string) => void;
	onUnshareFile: (fileId: string) => void;
	isAdmin: boolean;
}> = ({
	content,
	messageOfCurrentOrg,
	onShareFile,
	onUnshareFile,
	isAdmin,
}) => {
	const isPrivacyOn = useCentralStore(
		(state) => !!state.profile?.data_privacy_mode
	);
	const { files, loading, sharedUntil } = useFileHandler(
		content.file_ids || []
	);

	return (
		<div className="flex flex-col w-full gap-3">
			<div className="text-lg font-semibold text-gray-700">
				Neue Auftragsanfrage
			</div>
			<div className="flex flex-col w-full gap-2">
				<div className="flex items-start gap-2">
					<span className="text-sm font-medium text-gray-600">
						Datum:
					</span>
					<span className="text-sm text-gray-700">
						{content.incoming_order_date
							? new Date(
									content.incoming_order_date
								).toLocaleDateString("de-DE", {
									day: "2-digit",
									month: "2-digit",
									year: "numeric",
									hour: "2-digit",
									minute: "2-digit",
								})
							: "Kein Datum"}
					</span>
				</div>
				<div className="flex items-start gap-2">
					<span className="text-sm font-medium text-gray-600">
						Betreff:
					</span>
					<span className="text-sm text-gray-700">
						{content.title}
					</span>
				</div>

				<div className="flex items-start gap-2">
					<span className="text-sm font-medium text-gray-600">
						Beschreibung:
					</span>
					<span className="text-sm text-gray-700">
						{content.description}
					</span>
				</div>
				{(content.patient_first_name || content.patient_last_name) && (
					<div className="flex items-start gap-2">
						<span className="text-sm font-medium text-gray-600">
							Patient:
						</span>
						<span className="text-sm text-gray-700">
							{createSecurePatientName(
								null,
								content?.patient_first_name ?? "",
								content?.patient_last_name ?? "",
								isPrivacyOn
							)}
							{content?.patient_code
								? ` (${content?.patient_code})`
								: ""}
						</span>
					</div>
				)}
				{content.odontogram_teeth && (
					<div className="flex flex-col gap-2 font-medium text-gray-600">
						<OdontogramInMessage
							fieldValue={content.odontogram_teeth}
							fieldId="odontogram_teeth"
							references={{
								odontogram_teeth: "Zahnschema:",
								teeth: "Informationen",
							}}
						/>
					</div>
				)}
				{content.file_ids && content.file_ids.length > 0 && (
					<div className="flex flex-col w-full gap-2">
						<span className="text-sm font-medium text-gray-600">
							Dateien:
						</span>
						{loading ? (
							<div className="flex flex-wrap gap-2">
								{files.map((file) => (
									<div
										key={file.id}
										className="bg-gray-50 h-36 w-36 flex items-center justify-center border border-gray-200 rounded-md animate-pulse"
									>
										<Loading type="skeleton" />
									</div>
								))}
							</div>
						) : (
							<div className="flex flex-col w-full justify-center">
								<FileGalleryInMessage
									files={files}
									sharedUntil={sharedUntil}
									messageOfCurrentOrg={messageOfCurrentOrg}
									onShareFile={onShareFile}
									onUnshareFile={onUnshareFile}
									isAdmin={isAdmin}
								/>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

const JobRequestDeniedBlock: React.FC = () => {
	return (
		<div className="text-sm text-gray-900 leading-relaxed italic">
			...hat den Auftrag abgelehnt.
		</div>
	);
};

const JobRequestAcceptedBlock: React.FC = () => {
	return (
		<div className="text-sm text-gray-900 leading-relaxed italic">
			...hat den Auftrag akzeptiert.
		</div>
	);
};

const ConnectionRequestAcceptedBlock: React.FC = () => {
	return (
		<div className="text-sm text-gray-900 leading-relaxed italic">
			... nimmt Aufträge an.
		</div>
	);
};

const ConnectionRequestDeniedBlock: React.FC = () => {
	return (
		<div className="text-sm text-gray-900 leading-relaxed italic">
			... nimmt keine Aufträge mehr an.
		</div>
	);
};

const TextMessageBlock: React.FC<{ content: TextMessage }> = ({ content }) => {
	// Sanitize HTML content before parsing
	// TODO: Sanitize everywhere
	const sanitizedText = DOMPurify.sanitize(content.text ?? "");
	return (
		<div className="text-sm text-gray-900 leading-relaxed">
			{parse(sanitizedText)}
		</div>
	);
};
